import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const { selectedBusiness, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [qualityRating, setQualityRating] = useState(null);
  const dateQuery = dateFrom && dateTo ? `&date_from=${dateFrom}&date_to=${dateTo}` : '';
  const creditsLeft = (user?.message_limit || 0) - (user?.messages_used || 0);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    setLoading(true);
    const interval = setInterval(() => {
      api.get(`/dashboard/stats?business_id=${selectedBusiness.id}`).then(r => setStats({
        contacts: r.data.total_contacts || 0,
        messages: r.data.total_messages || 0,
        templates: r.data.total_templates || 0,
        sent: r.data.total_messages || 0,
        delivered: r.data.total_delivered || 0,
        read: r.data.total_read || 0,
        failed: r.data.total_failed || 0,
        today_messages: r.data.today_messages || 0,
        week_contacts: r.data.week_contacts || 0,
        unread: r.data.total_unread || 0,
        sla_pending: r.data.sla_pending || 0,
        billing_alert: r.data.billing_alert || null,
      })).catch(() => {});
    }, 30000);
    Promise.all([
      api.get(`/dashboard/stats?business_id=${selectedBusiness.id}${dateQuery}`),
      api.get(`/campaigns?business_id=${selectedBusiness.id}${dateQuery}`).catch(() => ({ data: [] })),
    ]).then(([r, c]) => {
      const d = r.data || {};
      setStats({
        contacts: d.total_contacts || 0,
        messages: d.total_messages || 0,
        templates: d.total_templates || 0,
        sent: d.total_messages || 0,
        delivered: d.total_delivered || 0,
        read: d.total_read || 0,
        failed: d.total_failed || 0,
        today_messages: d.today_messages || 0,
        week_contacts: d.week_contacts || 0,
        unread: d.total_unread || 0,
        sla_pending: d.sla_pending || 0,
        billing_alert: d.billing_alert || null,
      });
      setCampaigns(Array.isArray(c.data) ? c.data.slice(0, 3) : []);
    }).catch(() => {
      setStats({ contacts:0,messages:0,templates:0,sent:0,delivered:0,read:0,failed:0,today_messages:0,week_contacts:0,unread:0 });
    }).finally(() => setLoading(false));
    return () => clearInterval(interval);
  }, [selectedBusiness, dateQuery]);

  useEffect(() => {
    if (!selectedBusiness?.id) { setQualityRating(null); return; }
    api.get('/connect/profile', { params: { business_id: selectedBusiness.id } })
      .then(r => setQualityRating(r.data?.quality_rating || null))
      .catch(() => setQualityRating(null));
  }, [selectedBusiness]);

  const today = new Date().toLocaleDateString('en-US', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const bizName = selectedBusiness?.name || user?.business_name || 'there';
  const getHour = new Date().getHours();
  const greeting = getHour < 12 ? 'Good morning' : getHour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading || !stats) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh', fontSize:14, color:'#9ca3af' }}>
      Loading...
    </div>
  );

  const delivered = stats.delivered || 0;
  const read = stats.read || 0;
  const failed = stats.failed || 0;
  const total = Math.max(delivered + read + failed, 1);

  const statusBadge = (status) => {
    const map = {
      sent:      { bg:'#dcfce7', color:'#166534', label:'Sent' },
      pending:   { bg:'#fef9c3', color:'#854d0e', label:'Pending' },
      scheduled: { bg:'#ede9fe', color:'#5b21b6', label:'Scheduled' },
      failed:    { bg:'#fee2e2', color:'#b91c1c', label:'Failed' },
    };
    const s = map[status?.toLowerCase()] || map['pending'];
    return (
      <span style={{ background:s.bg, color:s.color, fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
        {s.label}
      </span>
    );
  };

  return (
    <div style={{ padding:'24px 28px', fontFamily:'DM Sans, sans-serif', background:'#f8f9fb', minHeight:'100vh' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:'#111827' }}>{greeting}, {bizName} 👋</div>
          <div style={{ fontSize:13, color:'#9ca3af', marginTop:2 }}>{today}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding:'6px 10px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:12, outline:'none', color:'#374151' }} />
          <span style={{ color:'#9ca3af', fontSize:12 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding:'6px 10px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:12, outline:'none', color:'#374151' }} />
          {dateFrom && dateTo && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ padding:'6px 10px', borderRadius:8, border:'none', background:'#fee2e2', color:'#991b1b', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Clear
            </button>
          )}
          {qualityRating && (() => {
            const qc = { GREEN: { bg:'#ecfdf5', border:'#6ee7b7', dot:'#22c55e', text:'#065f46', label:'High' }, YELLOW: { bg:'#fffbeb', border:'#fde68a', dot:'#f59e0b', text:'#92400e', label:'Medium' }, RED: { bg:'#fef2f2', border:'#fecaca', dot:'#ef4444', text:'#991b1b', label:'Low' } }[qualityRating] || null;
            if (!qc) return null;
            return (
              <div style={{ display:'flex', alignItems:'center', gap:5, background:qc.bg, border:`1px solid ${qc.border}`, borderRadius:999, padding:'6px 12px' }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:qc.dot, display:'inline-block' }} />
                <span style={{ fontSize:12, fontWeight:700, color:qc.text }}>Quality: {qc.label}</span>
              </div>
            );
          })()}
          {user?.role !== 'admin' && (
            <div style={{ background:'#ede9fe', borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:700, color:'#5b21b6' }}>
              Credits Left: {Number(creditsLeft).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {stats?.billing_alert && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>⚠️</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#b91c1c' }}>
                Payment pending — {stats.billing_alert.failed_count} messages failed to send
              </div>
              <div style={{ fontSize:12, color:'#7f1d1d' }}>
                WhatsApp is blocked until Meta's balance is paid.
              </div>
            </div>
          </div>
          {stats.billing_alert.waba_id && (
            <a
              href={`https://business.facebook.com/latest/billing_hub/accounts/details/?asset_id=${stats.billing_alert.waba_id}`}
              target="_blank" rel="noreferrer"
              style={{ background:'#dc2626', color:'#fff', border:'none', padding:'7px 16px', borderRadius:8, fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}
            >
              Pay Now
            </a>
          )}
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:16 }}>
        {[
          { label:'Total Contacts',  value:stats.contacts,          color:'#16a34a', sub:'+'+stats.week_contacts+' this week', bg:'#f0fdf4', border:'#bbf7d0', path:'/contacts' },
          { label:'Messages Sent',   value:stats.sent,              color:'#7c3aed', sub:'+'+stats.today_messages+' today',    bg:'#faf5ff', border:'#ddd6fe', path:'/campaigns' },
          { label:'Templates',       value:stats.templates,         color:'#2563eb', sub:'created',                            bg:'#eff6ff', border:'#bfdbfe', path:'/templates' },
          { label:'Campaigns',       value:campaigns.length,        color:'#d97706', sub:'total',                              bg:'#fffbeb', border:'#fde68a', path:'/campaigns' },
          { label:'Unread Inbox',    value:stats.unread||0,         color:'#dc2626', sub:'unread messages',                    bg:'#fff1f2', border:'#fecaca', path:'/inbox' },
        ].map(card => (
          <div key={card.label} onClick={() => navigate(card.path)} style={{ background:card.bg, borderRadius:12, padding:'14px 16px', border:`1px solid ${card.border}`, cursor:'pointer' }}>
            <div style={{ fontSize:11, color:'#6b7280', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:8 }}>
              {card.label}
            </div>
            <div style={{ fontSize:28, fontWeight:800, color:card.color, lineHeight:1 }}>
              {Number(card.value||0).toLocaleString()}
            </div>
            <div style={{ fontSize:12, color:card.color, marginTop:6, opacity:0.8 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Middle Row ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>

        {/* Message Activity */}
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #f0f0f0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:16 }}>Message Activity</div>
          {[
            { label:'Delivered', value:delivered, color:'#16a34a', bg:'#dcfce7', pct:Math.round((delivered/total)*100) },
            { label:'Read',      value:read,      color:'#7c3aed', bg:'#ede9fe', pct:Math.round((read/total)*100) },
            { label:'Failed',    value:failed,    color:'#ef4444', bg:'#fee2e2', pct:Math.max(Math.round((failed/total)*100), failed>0?3:0) },
          ].map(item => (
            <div key={item.label} style={{ marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:13, color:'#6b7280' }}>{item.label}</span>
                <span style={{ fontSize:13, fontWeight:700, color:item.color }}>{Number(item.value).toLocaleString()}</span>
              </div>
              <div style={{ background:'#f3f4f6', borderRadius:20, height:8 }}>
                <div style={{ background:item.color, height:8, borderRadius:20, width:`${item.pct}%`, transition:'width 0.5s ease' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Recent Campaigns */}
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #f0f0f0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:16 }}>Recent Campaigns</div>
          {campaigns.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#9ca3af', fontSize:13 }}>No campaigns yet</div>
          ) : campaigns.map((c, i) => (
            <div key={i} style={{ padding:'10px 12px', background:'#f9fafb', borderRadius:8, marginBottom:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{c.campaign_name}</div>
                  <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>{c.total_contacts||0} contacts</div>
                </div>
                {statusBadge(c.status||'sent')}
              </div>
              <div style={{ display:'flex', gap:10, marginTop:8, fontSize:11 }}>
                <span style={{ color:'#16a34a' }}>✓ {c.delivered_count||0} delivered</span>
                <span style={{ color:'#7c3aed' }}>👁 {c.read_count||0} read</span>
                {(c.failed_count||0) > 0 && <span style={{ color:'#ef4444' }}>✕ {c.failed_count} failed</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {stats.sla_pending > 0 && (
        <div onClick={() => navigate('/leads')} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'12px 16px', marginBottom:16, cursor:'pointer' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>⏰</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#92400e' }}>{stats.sla_pending} lead{stats.sla_pending>1?'s':''} need follow-up</div>
              <div style={{ fontSize:12, color:'#b45309' }}>SLA warning or exceeded — click to view</div>
            </div>
          </div>
          <span style={{ fontSize:18, color:'#b45309' }}>→</span>
        </div>
      )}
      {/* ── Quick Actions ── */}
      <div style={{ background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #f0f0f0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>Quick Actions</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
          {[
            { icon:'📣', label:'New Campaign',  bg:'#f0fdf4', border:'#bbf7d0', color:'#166534', path:'/campaigns' },
            { icon:'📝', label:'Add Template',  bg:'#faf5ff', border:'#ddd6fe', color:'#5b21b6', path:'/templates' },
            { icon:'👥', label:'Add Contact',   bg:'#eff6ff', border:'#bfdbfe', color:'#1d4ed8', path:'/contacts' },
            { icon:'💬', label:'Open Inbox',    bg:'#fefce8', border:'#fef08a', color:'#854d0e', path:'/inbox' },
          ].map(action => (
            <div key={action.label} onClick={() => navigate(action.path)}
              style={{ padding:'12px 10px', borderRadius:10, background:action.bg, border:`1px solid ${action.border}`, textAlign:'center', cursor:'pointer', transition:'transform 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
            >
              <div style={{ fontSize:22, marginBottom:6 }}>{action.icon}</div>
              <div style={{ fontSize:13, fontWeight:700, color:action.color }}>{action.label}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
